<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.products' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.products") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ product?.name || 'Product Details' }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-200">
            
            <!-- Loading State with Skeleton -->
            <div v-if="loading">
                <!-- Header Skeleton -->
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                    <div>
                        <div class="tw-w-48 tw-h-8 tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-mb-2"></div>
                        <div class="tw-w-80 tw-h-4 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                    </div>
                    
                    <!-- Action Buttons Skeleton -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-32 tw-h-10 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                        <div class="tw-w-28 tw-h-10 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                    </div>
                </div>

                <!-- Content Skeleton -->
                <div class="tw-w-full">
                    <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                        <!-- Left Column Skeleton - Product Information -->
                        <div class="xl:tw-col-span-8 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Product Information Header -->
                                <div class="tw-w-48 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-6"></div>
                                
                                <div class="tw-space-y-6">
                                    <!-- Product Name Skeleton -->
                                    <div>
                                        <div class="tw-w-28 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-h-12 tw-bg-gray-50 tw-rounded-md tw-border tw-p-3">
                                            <div class="tw-w-3/4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </div>
                                    </div>

                                    <!-- Description Skeleton -->
                                    <div>
                                        <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-min-h-[120px] tw-bg-gray-50 tw-rounded-md tw-border tw-p-3 tw-space-y-2">
                                            <div class="tw-w-full tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-w-5/6 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-w-4/5 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </div>
                                    </div>

                                    <!-- Categories Skeleton -->
                                    <div>
                                        <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-full tw-h-12 tw-bg-gray-50 tw-rounded-md tw-border tw-p-3 tw-flex tw-gap-2">
                                            <div class="tw-w-20 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                                            <div class="tw-w-24 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                                            <div class="tw-w-16 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                                        </div>
                                    </div>

                                    <!-- Statistics Skeleton -->
                                    <div>
                                        <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-grid tw-grid-cols-2 tw-gap-4">
                                            <!-- Views Skeleton -->
                                            <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-text-center">
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mx-auto tw-mb-1"></div>
                                                <div class="tw-w-12 tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mx-auto"></div>
                                            </div>
                                            <!-- Bookmarks Skeleton -->
                                            <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-text-center">
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mx-auto tw-mb-1"></div>
                                                <div class="tw-w-16 tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mx-auto"></div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Dates Skeleton -->
                                    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                                        <!-- Created Date Skeleton -->
                                        <div>
                                            <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                            <div class="tw-w-full tw-h-12 tw-bg-gray-50 tw-rounded-md tw-border tw-p-3">
                                                <div class="tw-w-3/4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                            </div>
                                        </div>
                                        
                                        <!-- Updated Date Skeleton -->
                                        <div>
                                            <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                            <div class="tw-w-full tw-h-12 tw-bg-gray-50 tw-rounded-md tw-border tw-p-3">
                                                <div class="tw-w-3/4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Company Information Skeleton -->
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Company Header -->
                                <div class="tw-w-40 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-4"></div>
                                
                                <!-- Company Content -->
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <!-- Company Logo Skeleton -->
                                    <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
                                    
                                    <!-- Company Details Skeleton -->
                                    <div class="tw-flex-1">
                                        <div class="tw-w-48 tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
                                        <div class="tw-w-32 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>

                            <!-- Created By Skeleton -->
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <div class="tw-w-20 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-4"></div>
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-flex-shrink-0"></div>
                                    <div class="tw-flex-1 tw-space-y-2">
                                        <div class="tw-w-3/4 tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        <div class="tw-w-1/2 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column Skeleton - Product Image -->
                        <div class="xl:tw-col-span-4 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <!-- Image Section Header -->
                                <div class="tw-w-32 tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-6"></div>
                                
                                <!-- Image Skeleton -->
                                <div class="tw-w-full tw-h-80 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-flex tw-items-center tw-justify-center">
                                    <div class="tw-w-24 tw-h-24 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div v-else>
                <!-- Header Section -->
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Product Details</h1>
                        <p class="tw-text-sm tw-text-gray-600">View information for "{{ product?.name || 'Loading...' }}"</p>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <a-button
                            @click="goBack"
                            intent="light"
                            left-icon="solar:arrow-left-linear"
                        >
                            Back to Products
                        </a-button>
                        
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.admin.products.edit', params: { id: $route.params.id } }"
                            intent="primary"
                            left-icon="solar:pen-bold-duotone"
                        >
                            Edit Product
                        </a-button>
                    </div>
                </div>



                <!-- Product Content -->
                <div v-if="product" class="tw-w-full">
                    <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                        <!-- Left Column - Product Information -->
                        <div class="xl:tw-col-span-8 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">Product Information</h2>
                                
                                <div class="tw-space-y-6">
                                    <!-- Product Name -->
                                    <div>
                                        <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Product Name</label>
                                        <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                            {{ product.name }}
                                        </div>
                                    </div>

                                    <!-- Product Description -->
                                    <div>
                                        <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Description</label>
                                        <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-min-h-[120px]">
                                            {{ product.description || 'No description provided' }}
                                        </div>
                                    </div>

                                    <!-- Categories -->
                                    <div>
                                        <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Categories</label>
                                        <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                            <div v-if="product.categories && product.categories.length > 0" class="tw-flex tw-gap-2 tw-flex-wrap">
                                                <span
                                                    v-for="category in product.categories"
                                                    :key="category.id"
                                                    class="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-bg-primary-100 tw-text-primary-800"
                                                >
                                                    {{ category.name }}
                                                </span>
                                            </div>
                                            <span v-else class="tw-text-gray-500">No categories assigned</span>
                                        </div>
                                    </div>

                                    <!-- Stats Row -->
                                    <div>
                                        <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Statistics</label>
                                        <div class="tw-grid tw-grid-cols-2 tw-gap-4">
                                            <!-- Views -->
                                            <div class="tw-p-4 tw-bg-gray-50 tw-rounded-md tw-border tw-text-center">
                                                <div class="tw-text-2xl tw-font-bold tw-text-primary-600">{{ product.views || 0 }}</div>
                                                <div class="tw-text-sm tw-text-gray-600">Views</div>
                                            </div>
                                            <!-- Bookmarks -->
                                            <div class="tw-p-4 tw-bg-gray-50 tw-rounded-md tw-border tw-text-center">
                                                <div class="tw-text-2xl tw-font-bold tw-text-yellow-600">{{ product.bookmarks || 0 }}</div>
                                                <div class="tw-text-sm tw-text-gray-600">Bookmarks</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Dates -->
                                    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                                        <!-- Created Date -->
                                        <div>
                                            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Created Date</label>
                                            <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                                {{ formatDate(product.created_at) }}
                                            </div>
                                        </div>
                                        
                                        <!-- Updated Date -->
                                        <div>
                                            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Last Updated</label>
                                            <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                                {{ formatDate(product.updated_at) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Company Information -->
                            <div v-if="product.company_name" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Company Information</h3>
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <!-- Company Logo -->
                                    <div class="tw-w-16 tw-h-16 tw-flex-shrink-0">
                                        <img
                                            v-if="product.company_logo_url"
                                            :src="product.company_logo_url"
                                            :alt="product.company_name"
                                            class="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200"
                                        />
                                        <div
                                            v-else
                                            class="tw-w-16 tw-h-16 tw-bg-gradient-to-br tw-from-primary-500 tw-to-primary-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-200"
                                        >
                                            <span class="tw-text-white tw-text-lg tw-font-semibold">
                                                {{ getCompanyInitials(product.company_name) }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <!-- Company Details -->
                                    <div class="tw-flex-1">
                                        <div class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-1">
                                            {{ product.company_name }}
                                        </div>
                                        <div class="tw-text-sm tw-text-gray-600">
                                            Company ID: {{ product.company_id || 'N/A' }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Created By -->
                            <div v-if="product.created_by" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Created By</h3>
                                <div class="tw-flex tw-items-center tw-gap-4">
                                    <!-- User Avatar -->
                                    <div class="tw-w-16 tw-h-16 tw-flex-shrink-0">
                                        <img
                                            v-if="product.created_by.profile_image"
                                            :src="product.created_by.profile_image"
                                            :alt="product.created_by.name"
                                            class="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200"
                                        />
                                        <div
                                            v-else
                                            class="tw-w-16 tw-h-16 tw-bg-gradient-to-br tw-from-gray-500 tw-to-gray-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-200"
                                        >
                                            <span class="tw-text-white tw-text-lg tw-font-semibold">
                                                {{ getUserInitials(product.created_by.name) }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <!-- User Info -->
                                    <div class="tw-flex-1">
                                        <div class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-1">
                                            {{ product.created_by.name }}
                                        </div>
                                        <div class="tw-text-sm tw-text-gray-600">
                                            {{ product.created_by.email }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column - Product Image -->
                        <div class="xl:tw-col-span-4 tw-space-y-6">
                            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">Product Image</h2>
                                
                                <!-- Image Display -->
                                <div class="tw-w-full tw-h-80 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                                    <img
                                        v-if="product.thumbnail_url"
                                        :src="product.thumbnail_url"
                                        :alt="product.name"
                                        class="tw-max-w-full tw-max-h-full tw-object-contain tw-rounded-lg"
                                    />
                                    <div v-else class="tw-text-center tw-text-gray-500">
                                        <icon icon="solar:box-bold-duotone" class="tw-text-6xl tw-text-gray-400 tw-mb-4" />
                                        <p class="tw-text-lg tw-font-medium">No Image Available</p>
                                        <p class="tw-text-sm">No image has been uploaded for this product</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else class="tw-text-center tw-py-12">
                    <div class="tw-text-gray-500">
                        <icon icon="solar:danger-circle-bold-duotone" class="tw-text-6xl tw-text-red-400 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-medium tw-mb-2">Product Not Found</h3>
                        <p class="tw-mb-4">The product you're looking for doesn't exist or has been deleted.</p>
                        <a-button
                            @click="goBack"
                            intent="primary"
                        >
                            Back to Products
                        </a-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import useAlert from '@/composables/useAlert'
import productApi from '@/api/products'

const router = useRouter()
const route = useRoute()

// State
const loading = ref(true)
const product = ref(null)

// Methods
const goBack = () => {
    router.push({ name: 'pages.admin.products' })
}

const formatDate = (date) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const getCompanyInitials = (companyName) => {
    if (!companyName) return '?'
    
    const words = companyName.trim().split(' ')
    if (words.length === 1) {
        return words[0].charAt(0).toUpperCase()
    }
    
    return words
        .slice(0, 2) // Take first 2 words
        .map(word => word.charAt(0).toUpperCase())
        .join('')
}

const getUserInitials = (userName) => {
    if (!userName) return '?'
    
    const words = userName.trim().split(' ')
    if (words.length === 1) {
        return words[0].charAt(0).toUpperCase()
    }
    
    return words
        .slice(0, 2) // Take first 2 words
        .map(word => word.charAt(0).toUpperCase())
        .join('')
}

const loadProduct = async () => {
    loading.value = true
    
    try {
        const productId = route.params.id
        console.log('Loading product with ID:', productId)
        
        // Use admin endpoint if available, otherwise fall back to regular endpoint
        const response = productApi.admin && productApi.admin.get 
            ? await productApi.admin.get(productId)
            : await productApi.get(productId)
        
        console.log('Product API Response:', response)
        console.log('Response data:', response.data)
        console.log('Response status:', response.status)
        
        if (response.data) {
            product.value = response.data.data
            console.log('Product set to:', product.value)
        } else {
            console.log('No data in response:', response)
            throw new Error('Product not found')
        }
    } catch (error) {
        console.error('Error loading product:', error)
        console.error('Error response:', error.response)
        console.error('Error message:', error.message)
        
        // Handle different error types
        if (error.response?.status === 404) {
            useAlert('Product not found', 'danger')
        } else if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to view this product.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Error loading product details. Please try again.', 'danger')
        }
        
        product.value = null
    } finally {
        loading.value = false
    }
}

// Initialize
onMounted(() => {
    loadProduct()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>